import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import accreditationPrintingService from '../../../services/accreditationPrintingService';

const POLL_MS = 1500;

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

const apiMessage = (e) => e.response?.data?.message || e.message;

const BulkCardDownload = ({ parentTypes, userTypes }) => {
  const [parentId, setParentId] = useState('');
  const [userTypeId, setUserTypeId] = useState('');
  const [info, setInfo] = useState(null); // { total, printable, byType }
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [job, setJob] = useState(null); // { job_id, status, total, done, skipped }
  const pollRef = useRef(null);
  const busy = job?.status === 'running';

  const children = useMemo(
    () => userTypes.filter((u) => Number(u.parent_category_id) === Number(parentId)),
    [userTypes, parentId]
  );

  useEffect(() => () => clearInterval(pollRef.current), []);

  useEffect(() => {
    setInfo(null);
    if (!parentId) return undefined;
    let stale = false;
    setLoadingInfo(true);
    accreditationPrintingService
      .getUsersCount({ parent_type_id: parentId, user_type_id: userTypeId || undefined })
      .then((res) => !stale && setInfo(res?.data || null))
      .catch((e) => !stale && Swal.fire('Error', apiMessage(e), 'error'))
      .finally(() => !stale && setLoadingInfo(false));
    return () => { stale = true; };
  }, [parentId, userTypeId]);

  const finish = async (status, jobId, skipped) => {
    clearInterval(pollRef.current);
    if (status === 'done') {
      try {
        saveBlob(await accreditationPrintingService.downloadZipFile(jobId), `accred_cards_${parentId}${userTypeId ? `_${userTypeId}` : ''}.zip`);
        if (skipped) Swal.fire('Downloaded', `${skipped} user(s) were skipped. See skipped.txt inside the ZIP.`, 'info');
      } catch (e) {
        Swal.fire('Download Failed', apiMessage(e), 'error');
      }
    } else if (status === 'failed') {
      Swal.fire('Generation Failed', 'The server could not build the ZIP. Check the server log.', 'error');
    }
  };

  const handleStart = async () => {
    try {
      const res = await accreditationPrintingService.startZipJob({
        parent_type_id: Number(parentId),
        user_type_id: userTypeId ? Number(userTypeId) : undefined,
      });
      setJob(res.data);
      pollRef.current = setInterval(async () => {
        try {
          const s = (await accreditationPrintingService.getZipJob(res.data.job_id)).data;
          setJob(s);
          if (s.status !== 'running') finish(s.status, s.job_id, s.skipped);
        } catch (e) {
          clearInterval(pollRef.current);
          setJob(null);
          Swal.fire('Job Lost', apiMessage(e), 'error');
        }
      }, POLL_MS);
    } catch (e) {
      Swal.fire('Could not start', apiMessage(e), 'error');
    }
  };

  const handleCancel = () => job && accreditationPrintingService.cancelZipJob(job.job_id).catch(() => {});

  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="card shadow-sm border-0">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0 fw-bold">Bulk Card Download</h5>
        <small className="text-muted">
          One accreditation card PDF per user in Sent For Printing, zipped.
        </small>
      </div>
      <div className="card-body">
        <div className="row g-3 mb-3">
          <div className="col-md-5">
            <label className="form-label fw-semibold small">Parent User Type *</label>
            <select
              className="form-select"
              value={parentId}
              onChange={(e) => { setParentId(e.target.value); setUserTypeId(''); }}
              disabled={busy}
            >
              <option value="">Select parent type</option>
              {parentTypes.map((p) => (
                <option key={p.category_id} value={p.category_id}>{p.category_name_view}</option>
              ))}
            </select>
          </div>
          <div className="col-md-5">
            <label className="form-label fw-semibold small">User Type (optional)</label>
            <select
              className="form-select"
              value={userTypeId}
              onChange={(e) => setUserTypeId(e.target.value)}
              disabled={!parentId || busy}
            >
              <option value="">All user types under parent</option>
              {children.map((u) => (
                <option key={u.sub_category_id} value={u.sub_category_id}>{u.sub_category_name_view}</option>
              ))}
            </select>
          </div>
        </div>

        {loadingInfo && <Spinner size="sm" animation="border" />}

        {info && (
          <>
            <table className="table table-sm align-middle mb-3">
              <thead>
                <tr><th>User Type</th><th className="text-end">Users Sent For Printing</th><th>Card design</th><th>Access permission (zones &amp; dining)</th></tr>
              </thead>
              <tbody>
                {info.byType.map((t) => (
                  <tr key={t.user_type_id}>
                    <td>{t.name}</td>
                    <td className="text-end">{t.count}</td>
                    <td>
                      {t.design === 'custom'
                        ? <span className="badge bg-primary">Custom template</span>
                        : <span className="badge bg-secondary">Standard card</span>}
                    </td>
                    <td>
                      {t.has_permission
                        ? <span className="badge bg-success">Set</span>
                        : <span className="badge bg-warning text-dark" title="Set it under Access Permission">Not set: zones and dining blank</span>}
                    </td>
                  </tr>
                ))}
                {!info.byType.length && (
                  <tr><td colSpan={4} className="text-muted text-center">No eligible users</td></tr>
                )}
              </tbody>
            </table>
            <div className="d-flex align-items-center gap-3">
              <button className="btn btn-primary" onClick={handleStart} disabled={busy || !info.printable}>
                <i className="fa fa-download me-1" /> Generate ZIP ({info.printable} cards)
              </button>
              {busy && (
                <>
                  <div className="progress flex-grow-1" style={{ maxWidth: 320, height: 20 }}>
                    <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${pct}%` }}>
                      {job.done}/{job.total}
                    </div>
                  </div>
                  <button className="btn btn-outline-danger btn-sm" onClick={handleCancel}>Cancel</button>
                </>
              )}
              {job?.status === 'cancelled' && <span className="text-muted small">Cancelled</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkCardDownload;
